import React, { useEffect, useState } from 'react';
import { Check, Copy, Gift, Sparkles, X } from 'lucide-react';

const STORAGE_KEY = 'alphapro-discount-popup-last-shown';
const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

function DiscountPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    try {
      const lastShown = Number(window.localStorage.getItem(STORAGE_KEY) || 0);
      const now = Date.now();

      if (!lastShown || now - lastShown >= THREE_DAYS_MS) {
        window.localStorage.setItem(STORAGE_KEY, String(now));
        setIsOpen(true);
      }
    } catch {
      setIsOpen(true);
    }
  }, []);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText('Alpha');
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/55 px-4 py-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="discount-popup-title"
    >
      <section className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white text-center shadow-2xl shadow-slate-950/35">
        <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-sky-400 via-blue-600 to-violet-500" />
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="absolute right-3 top-4 rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          aria-label="Zamknij ofertę rabatową"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>

        <div className="px-7 pb-7 pt-10 sm:px-10">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-100 to-blue-100 text-blue-600 shadow-sm">
            <Gift className="h-8 w-8" aria-hidden="true" />
          </span>
          <p className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Specjalna oferta dla Ciebie
          </p>
          <h2 id="discount-popup-title" className="mt-3 text-2xl font-bold tracking-tight text-slate-900">
            Gratulacje, trafiasz na kod rabatowy!
          </h2>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
            Użyj kodu poniżej i odbierz <strong className="font-semibold text-slate-900">10% rabatu</strong> na swoje zamówienie.
          </p>

          <div className="mt-6 rounded-2xl border border-dashed border-blue-300 bg-gradient-to-br from-sky-50 to-blue-50 p-4">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">Twój kod rabatowy</p>
            <p className="mt-1 text-3xl font-black tracking-[0.2em] text-blue-700">Alpha</p>
            <p className="mt-1 text-sm font-semibold text-slate-700">-10% na zamówienie</p>
          </div>

          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={copyCode}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
              {copied ? 'Kod skopiowany' : 'Skopiuj kod'}
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Nie teraz
            </button>
          </div>
          <p className="mt-4 text-xs text-slate-400">Oferta pojawi się ponownie najwcześniej za 3 dni.</p>
        </div>
      </section>
    </div>
  );
}

export default DiscountPopup;
